import { UploadOutlined, CopyOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Select,
  Button,
  Space,
  Divider,
  Typography,
  Upload,
  List,
  Switch,
  message
} from "antd";

import { copyTextToClipboard } from "../../../../../utils/clipboardHelper";

const { TextArea } = Input;
const { Title } = Typography;
const { Item } = Form;
const { Option } = Select;

export const FormStep2 = ({
  formStep2,
  handleSaveStep2,
  uploadingAsset,
  assets,
  uploadAssets,
  renderStepControls
}) => {
  const QUESTION_TYPES = {
    MCQ: "MCQ",
    SPOT: "SPOT"
  };

  const beforeUpload = async (file, fileList) => {
    // beforeUpload runs once per file; upload the full selected batch only once.
    if (file.uid !== fileList?.[0]?.uid) {
      return false;
    }

    await uploadAssets(fileList);
    return false;
  };

  const copyUrl = async (url) => {
    const hasCopied = await copyTextToClipboard(url);
    if (!hasCopied) {
      message.error("Failed to copy URL");
      return;
    }
    message.success("URL copied!");
  };

  return (
    <Form layout="vertical" form={formStep2} onFinish={handleSaveStep2}>
      <Title level={4}>Content</Title>
      <Item name="draftContent" label="Markdown Content" rules={[{ required: true }]}>
        <TextArea rows={12} placeholder="Write markdown content here..." />
      </Item>

      <Divider />
      <Title level={4}>Problems</Title>
      <Form.List name="problems">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name }) => (
              <Space key={key} align="baseline" wrap style={{ display: "flex", marginBottom: 8 }}>
                <Item name={[name, "name"]} rules={[{ required: true }]}>
                  <Input placeholder="Problem Name" />
                </Item>
                <Item name={[name, "platform"]}>
                  <Input placeholder="Codeforces / LeetCode" />
                </Item>
                <Item
                  name={[name, "link"]}
                  rules={[
                    { required: true, message: "Link required" },
                    { type: "url", message: "Please enter a valid URL" }
                  ]}
                >
                  <Input placeholder="https://example.com" />
                </Item>
                <Button type="link" onClick={() => remove(name)}>
                  Remove
                </Button>
              </Space>
            ))}
            <Button type="dashed" onClick={() => add()} block>
              + Add Problem
            </Button>
          </>
        )}
      </Form.List>

      <Divider />
      <Title level={4}>Resources</Title>
      <Form.List name="resources">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name }) => (
              <Space key={key} align="baseline" wrap style={{ display: "flex", marginBottom: 8 }}>
                <Item name={[name, "name"]}>
                  <Input placeholder="Resource Name" />
                </Item>
                <Item
                  name={[name, "link"]}
                  rules={[
                    { required: true, message: "Link required" },
                    { type: "url", message: "Please enter a valid URL" }
                  ]}
                >
                  <Input placeholder="https://example.com/resource" />
                </Item>

                <Button type="link" onClick={() => remove(name)}>
                  Remove
                </Button>
              </Space>
            ))}
            <Button type="dashed" onClick={() => add()} block>
              + Add Resource
            </Button>
          </>
        )}
      </Form.List>

      <Divider />
      <Title level={4}>Practice Questions</Title>
      <Form.List name="questions">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name }) => (
              <div
                key={key}
                style={{
                  border: "1px solid #ddd",
                  padding: 10,
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                <Item name={[name, "type"]} label="Type" rules={[{ required: true }]}>
                  <Select placeholder="Select type">
                    <Option value={QUESTION_TYPES.MCQ}>MCQ</Option>
                    <Option value={QUESTION_TYPES.SPOT}>SPOT</Option>
                  </Select>
                </Item>
                <Item name={[name, "question"]} label="Question" rules={[{ required: true }]}>
                  <Input placeholder="What is DP?" />
                </Item>

                <Item
                  noStyle
                  shouldUpdate={(prev, curr) =>
                    prev.questions?.[name]?.type !== curr.questions?.[name]?.type ||
                    prev.questions?.[name]?.options !== curr.questions?.[name]?.options
                  }
                >
                  {() => {
                    const questionType = formStep2.getFieldValue(["questions", name, "type"]);
                    const currentOptions =
                      formStep2.getFieldValue(["questions", name, "options"]) || [];

                    return questionType === QUESTION_TYPES.MCQ ? (
                      <>
                        <Item
                          name={[name, "options"]}
                          label="Options (for MCQs)"
                          rules={[{ required: true, message: "Options are required for MCQ" }]}
                        >
                          <Select
                            mode="tags"
                            placeholder="Type option and press Enter..."
                            style={{ width: "100%" }}
                          />
                        </Item>
                        <Item
                          name={[name, "answer"]}
                          label="Answer (Select from options)"
                          rules={[{ required: true, message: "Answer is required" }]}
                        >
                          <Select
                            placeholder={
                              currentOptions.length > 0
                                ? "Select the correct answer"
                                : "Add options first"
                            }
                            disabled={currentOptions.length === 0}
                          >
                            {currentOptions.map((option, idx) => (
                              <Option key={idx} value={option}>
                                {option}
                              </Option>
                            ))}
                          </Select>
                        </Item>
                      </>
                    ) : questionType === QUESTION_TYPES.SPOT ? (
                      <Item
                        name={[name, "answer"]}
                        label="Answer"
                        rules={[{ required: true, message: "Answer is required" }]}
                      >
                        <Input placeholder="Enter the answer" />
                      </Item>
                    ) : null;
                  }}
                </Item>
                <Item
                  name={[name, "reasonForAnswer"]}
                  label="Reason / Explanation (optional)"
                  rules={[{ required: false }]}
                >
                  <Input.TextArea placeholder="Explain the answer briefly..." rows={2} />
                </Item>
                <Button danger onClick={() => remove(name)}>
                  Remove Question
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={() => add()} block>
              + Add Question
            </Button>
          </>
        )}
      </Form.List>

      <Divider />
      <Title level={4}>Banner Image URL</Title>
      <Item name="bannerImageUrl" label="Banner Image URL">
        <Input placeholder="Enter banner image URL" />
      </Item>

      <Divider />
      <Title level={4}>Assets / Files</Title>
      <div style={{ marginBottom: 12 }}>
        <Upload beforeUpload={beforeUpload} multiple showUploadList={false}>
          <Button icon={<UploadOutlined />} loading={uploadingAsset}>
            Upload Asset
          </Button>
        </Upload>

        <div style={{ marginTop: 12 }}>
          <List
            bordered
            dataSource={assets}
            locale={{ emptyText: "No assets uploaded yet." }}
            renderItem={(item, idx) => (
              <List.Item
                actions={[
                  <Button
                    key={`open-${idx}`}
                    type="link"
                    onClick={() => window.open(item.url, "_blank")}
                  >
                    Open
                  </Button>,
                  <Button
                    key={`copy-${idx}`}
                    type="link"
                    icon={<CopyOutlined />}
                    onClick={() => copyUrl(item.url)}
                  >
                    Copy
                  </Button>
                ]}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    overflow: "hidden"
                  }}
                >
                  {item.url.match(/\.(jpeg|jpg|png|gif|svg)$/i) && (
                    <img
                      src={item.url}
                      alt={item.filename}
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: "cover",
                        borderRadius: 4,
                        flexShrink: 0
                      }}
                    />
                  )}
                  <div style={{ overflow: "hidden" }}>
                    <div style={{ fontWeight: 600 }}>{item.filename}</div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#555",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "80%"
                      }}
                      title={item.url}
                    >
                      {item.url}
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      </div>

      <Divider />
      <Title level={4}>Blog Metadata</Title>
      <Item name="isNewBlog" label="Mark as New Blog" valuePropName="checked" initialValue={false}>
        <Switch />
      </Item>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>{renderStepControls()}</div>
    </Form>
  );
};
